import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  additionalCostItemsService,
  type AddlCostBulkColumn,
  type AddlCostChangeAllPreviewRow,
  type AddlCostChangeAllResult,
} from '../../services/additionalCostItems.service';

interface Props {
  isOpen: boolean;
  /** Target PROs to fan the checked fields out to (their non-deleted cost records). */
  pros: (number | string)[];
  /** The flagged Addl Cost fields captured from the anchor modal. */
  fields: Partial<Record<AddlCostBulkColumn, unknown>>;
  onClose: () => void;
  /** Called after an apply run so the anchor panel can refetch. */
  onApplied?: () => void;
}

/**
 * Addl Cost Change All — review + results modal (recno-keyed).
 *
 * Cloned from the order-arm ChangeAllReviewModal but per-RECORD: a PRO can have
 * several cost records, so preview/results are per additionalcost recno. The set is
 * small (cost records per PRO), so apply is a single call rather than chunked. The
 * backend re-validates every record and skips ineligible ones (exported / incomplete
 * approval / …) without aborting the batch.
 */
const AddlCostChangeAllReviewModal: React.FC<Props> = ({
  isOpen, pros, fields, onClose, onApplied,
}) => {
  const [step, setStep] = useState<'review' | 'results'>('review');
  const [rows, setRows] = useState<AddlCostChangeAllPreviewRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [results, setResults] = useState<AddlCostChangeAllResult[] | null>(null);
  const applyingRef = useRef(false);

  // Reset when closed so reopening for a new set starts fresh at review.
  useEffect(() => {
    if (isOpen) return;
    setStep('review'); setRows(null); setPreviewLoading(false); setPreviewError(null);
    setResults(null); setApplyError(null); setApplying(false);
    applyingRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== 'review') return;
    const ctrl = new AbortController();
    setPreviewLoading(true); setPreviewError(null); setRows(null);
    additionalCostItemsService.changeAllPreview(pros, fields, ctrl.signal)
      .then((res) => setRows(res.rows))
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setPreviewError(detail || 'Could not check the target cost records.');
        setRows([]);
      })
      .finally(() => { if (!ctrl.signal.aborted) setPreviewLoading(false); });
    return () => ctrl.abort();
  }, [isOpen, step, pros, fields]);

  const eligible = useMemo(() => (rows ?? []).filter((r) => r.eligible), [rows]);
  const skipped = useMemo(() => (rows ?? []).filter((r) => !r.eligible), [rows]);
  const fieldColumns = useMemo(() => Object.keys(fields), [fields]);

  const handleApply = async () => {
    if (applyingRef.current || eligible.length === 0) return;
    applyingRef.current = true;
    setApplying(true); setApplyError(null);
    const nonce =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `aca-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const res = await additionalCostItemsService.changeAllApply(pros, fields, nonce);
      setResults(res.results || []);
      setStep('results');
      onApplied?.();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setApplyError(detail || 'Could not apply the changes.');
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  // Alt+C closes (never mid-apply). Apply gets NO chord on purpose: it fans the
  // change out to every cost record on every target PRO, and this review step
  // exists to slow that down — a keystroke away defeats it. Claiming the layer
  // also stops Alt+S reaching the cost-item modal that stays mounted below.
  useAltHotkeys({ C: applying ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const handleClose = () => { if (!applyingRef.current) onClose(); };
  const applied = (results ?? []).filter((r) => r.applied);
  const notApplied = (results ?? []).filter((r) => !r.applied);
  const label = (r: { pronumber: number | string; recno: number }) => `${r.pronumber} · #${r.recno}`;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim/40 p-4" onMouseDown={handleClose}>
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-[min(880px,96vw)] max-h-[90vh] flex flex-col"
        role="dialog" aria-label="Addl Cost Change All" onMouseDown={(e) => e.stopPropagation()}
      >
        <ModalHeader
          className="shrink-0"
          title={`Addl Cost Change All — ${pros.length} pro${pros.length === 1 ? '' : 's'}`}
          onClose={handleClose}
          dragHandleProps={dragHandleProps}
        />

        {step === 'review' && (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="text-sm font-semibold text-slate-800 dark:text-gray-100 shrink-0">
              Apply these Addl Cost fields to every cost record on {pros.length} pro{pros.length === 1 ? '' : 's'}
            </div>

            {fieldColumns.length > 0 && (
              <div className="shrink-0">
                <div className="text-xs font-medium text-slate-600 dark:text-gray-300 mb-1">Fields to apply:</div>
                <div className="flex flex-wrap gap-1.5">
                  {fieldColumns.map((col) => (
                    <span key={col} className="inline-flex items-center rounded bg-slate-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-slate-700 dark:text-gray-200">
                      {col}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {previewError ? (
              <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">{previewError}</div>
            ) : previewLoading ? (
              <div className="py-10 text-center text-sm text-slate-500 dark:text-gray-400">Checking cost records…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 shrink-0">
                  <div className="rounded border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2">
                    <div className="text-lg font-semibold text-green-700 dark:text-green-300">{eligible.length}</div>
                    <div className="text-xs text-green-700 dark:text-green-400">records will change</div>
                  </div>
                  <div className="rounded border border-slate-300 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/30 px-3 py-2">
                    <div className="text-lg font-semibold text-slate-600 dark:text-gray-300">{skipped.length}</div>
                    <div className="text-xs text-slate-500 dark:text-gray-400">cannot change</div>
                  </div>
                </div>

                {skipped.length > 0 && (
                  <div className="min-h-0 overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                    <table className="w-full text-[0.75rem]">
                      <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                        <tr>
                          <th className="text-left font-medium px-2 py-1.5 w-40">Pro · Record</th>
                          <th className="text-left font-medium px-2 py-1.5">Cannot change — reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipped.map((r) => (
                          <tr key={r.recno} className="border-t border-slate-100 dark:border-gray-700">
                            <td className="px-2 py-1 tabular-nums text-slate-700 dark:text-gray-200">{label(r)}</td>
                            <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{r.errors.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {applyError && (
                  <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">{applyError}</div>
                )}
              </>
            )}

            <div className="flex items-center justify-end gap-2 pt-1 shrink-0">
              <button type="button" onClick={handleClose} disabled={applying}
                className="px-3 py-1.5 rounded border border-slate-300 dark:border-gray-600 text-sm hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
              <button type="button" onClick={handleApply}
                disabled={applying || previewLoading || !!previewError || eligible.length === 0}
                title={eligible.length === 0 ? 'No eligible cost records' : 'Apply the changes'}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {applying ? 'Applying…' : `Apply to ${eligible.length} record${eligible.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}

        {step === 'results' && (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="text-sm text-slate-700 dark:text-gray-200 shrink-0">Changes applied.</div>
            <div className="grid grid-cols-2 gap-3 shrink-0">
              <div className="rounded border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                <span className="text-sm text-green-700 dark:text-green-300">{applied.length} changed</span>
              </div>
              <div className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-sm text-amber-700 dark:text-amber-300">{notApplied.length} skipped</span>
              </div>
            </div>

            {notApplied.length > 0 && (
              <div className="min-h-0 overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                <table className="w-full text-[0.75rem]">
                  <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                    <tr>
                      <th className="text-left font-medium px-2 py-1.5 w-40">Pro · Record</th>
                      <th className="text-left font-medium px-2 py-1.5">Skipped — reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notApplied.map((r) => (
                      <tr key={r.recno} className="border-t border-slate-100 dark:border-gray-700">
                        <td className="px-2 py-1 tabular-nums text-slate-700 dark:text-gray-200">{label(r)}</td>
                        <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{r.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end pt-1 shrink-0">
              <button type="button" onClick={onClose} className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AddlCostChangeAllReviewModal;
